'use client';

import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { LargeCollapsableRotate } from '../collapsable/collapsable';
import { ReverseCustomCheckBox } from '../form/form';
import { H1, H4 } from '../styled/typography';
import { FilterQuery, FilterTitles } from '../../models/filters';
import { Category, CategoryData } from '../../models/action';

const defaultCountState = { categoryId: null, count: null };

export default function CategoryFilter({
  query,
  categories,
  setFilterTitles,
  setSeoCategory,
  setQuery,
  eventCount,
}: {
  query: FilterQuery;
  categories: Category[];
  setFilterTitles: Dispatch<SetStateAction<FilterTitles>>;
  setSeoCategory: (category: CategoryData | null) => void;
  setQuery: (query: any) => void;
  eventCount: number;
}) {
  const [openedCategory, setOpenedCategory] = useState<number | [] | null>(
    null
  );
  const [checkedCount, setCheckedCount] = useState<{
    categoryId: number | null;
    count: number | null;
  }>(defaultCountState);

  useEffect(() => {
    const { category, subcategories } = query.filters.categories;

    const categoryData = categories.find((c) => c.id === category);

    if (typeof category !== 'number') {
      setCheckedCount(defaultCountState);
      return;
    }

    if (subcategories.length === categoryData?.children.length) {
      setCheckedCount({
        categoryId: category,
        count: categoryData.count,
      });
    } else {
      const children = categories.map((c) => c.children).flat();
      const count = subcategories
        .map((item) => children.find((child) => child.id === item))
        .reduce((acc, cur) => acc + (cur?.count || 0), 0);
      setCheckedCount({
        categoryId: category,
        count,
      });
    }
  }, [query]);

  const checkCategory = (category: Category, subcategories: CategoryData[]) => {
    if (
      category.id === query.filters.categories.category &&
      category.children.length === query.filters.categories.subcategories.length
    ) {
      setQuery({
        ...query,
        filters: {
          ...query.filters,
          categories: {
            category: [],
            subcategories: [],
          },
          pages: [],
        },
        isDataFromServer: false,
      });
      setSeoCategory(null);
      setFilterTitles((prev) => ({ ...prev, category: null }));
    } else {
      setQuery({
        ...query,
        filters: {
          ...query.filters,
          categories: {
            category: category.id,
            subcategories: subcategories.map((item) => item.id),
          },
          pages: [],
        },
        isDataFromServer: false,
      });
      setFilterTitles((prev) => ({
        ...prev,
        category: `${category.title}: ${subcategories
          .map((c) => c.title)
          .join(', ')}`,
      }));
      setSeoCategory(category);
    }
  };

  const checkSubcategory = (idCategory: number, subcategory: CategoryData) => {
    const category = categories.find((item) => item.id === idCategory);
    if (idCategory === query.filters.categories.category) {
      const newSubcategories = [...query.filters.categories.subcategories];
      if (newSubcategories.indexOf(subcategory.id) === -1) {
        newSubcategories.push(subcategory.id);
        setSeoCategory(
          category?.children.length === newSubcategories.length
            ? category
            : subcategory
        );
        const currentSeoCategory = categories.find(
          (item) => Number(item.id) === Number(idCategory)
        );
        setSeoCategory(currentSeoCategory || null);
      } else {
        newSubcategories.splice(newSubcategories.indexOf(subcategory.id), 1);
        if (newSubcategories.length) {
          if (category) {
            const subcategoryId = newSubcategories[newSubcategories.length - 1];
            const prevSubcategory = category.children.find(
              (item) => item.id === subcategoryId
            );
            if (newSubcategories.length > 1) {
              const currentCheckCategory = categories.find(
                (item) => item.id === idCategory
              );
              setSeoCategory(currentCheckCategory || null);
            } else {
              const currentSeoCategory = categories.find(
                (item) => Number(item.id) === Number(idCategory)
              );
              if (
                currentSeoCategory &&
                currentSeoCategory.children.length > 1
              ) {
                setSeoCategory(prevSubcategory || null);
              } else {
                setSeoCategory(currentSeoCategory || null);
              }
            }
          }
        } else {
          setSeoCategory(null);
        }
      }
      setQuery({
        ...query,
        filters: {
          ...query.filters,
          categories: {
            category: newSubcategories.length > 0 ? idCategory : [],
            subcategories: newSubcategories,
          },
          pages: [],
        },
        isDataFromServer: false,
      });
      setFilterTitles((prev) => ({
        ...prev,
        category: `${
          (query.filters.categories.category as any)?.title
        }: ${newSubcategories.map((c: any) => c.title).join(', ')}`,
      }));
    } else {
      setQuery({
        ...query,
        filters: {
          ...query.filters,
          categories: {
            category: idCategory,
            subcategories: [subcategory.id],
          },
          pages: [],
        },
        isDataFromServer: false,
      });
      setFilterTitles((prev) => ({
        ...prev,
        category: `${categories.find((c) => c.id === idCategory)?.title}: ${
          subcategory.title
        }`,
      }));
      const currentSeoCategory = categories.find(
        (item) => Number(item.id) === Number(idCategory)
      );
      if (currentSeoCategory && currentSeoCategory.children.length > 1) {
        setSeoCategory(subcategory);
      } else {
        setSeoCategory(currentSeoCategory || null);
      }
    }
  };

  return (
    <div>
      <H1
        css={{
          marginBottom: 14,
          fontSize: '24px !important',
          fontWeight: 900,
          fontFamily: 'Montserrat',
        }}
      >
        Категория
      </H1>

      <div
        css={{
          boxShadow: '0 1px 1px rgba(0, 0, 0, 0.05)',
          height: 2,
          color: '#000',
        }}
      />
      {categories.map(
        (category) =>
          !!category.count && (
            <LargeCollapsableRotate
              key={`CollapsableRotateAnimate${category.id}`}
              isCollapse={openedCategory === category.id}
              callback={() =>
                setOpenedCategory(
                  openedCategory === category.id ? null : category.id
                )
              }
              head={
                <H4
                  css={{
                    margin: '8px 0',
                    fontSize: 18,
                    fontWeight: 700,
                    fontFamily: 'Montserrat',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    width: '90vw',
                    textAlign: 'start',
                    color: '#333',
                  }}
                >
                  {category.title}{' '}
                  <span css={{ color: '#BFC1C3' }}>
                    {category.id === checkedCount?.categoryId &&
                      !!eventCount &&
                      eventCount}
                  </span>
                </H4>
              }
              body={
                <div
                  css={{
                    fontSize: 18,
                    fontWeight: 700,
                    color: '#000',
                    fontFamily: 'Montserrat',
                    label: {
                      margin: '20px 0',
                    },
                    '.category-filter__count': {
                      fontFamily: 'Montserrat',
                      color: '#BFC1C3',
                      fontWeight: 700,
                    },
                  }}
                >
                  <ReverseCustomCheckBox
                    value={String(category.id)}
                    callback={() => checkCategory(category, category.children)}
                    label={`Все <span class="category-filter__count">${category.count}</span>`}
                    checked={
                      query.filters.categories.category === category.id &&
                      query.filters.categories.subcategories.length ===
                        category.children.length
                    }
                  />
                  {category.children.map(
                    (subcategory) =>
                      !!subcategory.count && (
                        <ReverseCustomCheckBox
                          key={`CustomCheckBox__${subcategory.id}`}
                          value={String(subcategory.id)}
                          callback={() =>
                            checkSubcategory(category.id, subcategory)
                          }
                          label={`${subcategory.title} <span class="category-filter__count">${subcategory.count}</span>`}
                          checked={
                            query.filters.categories.subcategories.indexOf(
                              subcategory.id
                            ) !== -1
                          }
                        />
                      )
                  )}
                </div>
              }
            />
          )
      )}
    </div>
  );
}
